import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "@components/shadcn-ui/card";
import { Button } from "@components/shadcn-ui/button";
import { Badge } from "@components/shadcn-ui/badge";
import { useToast } from "@customHooks/useToast";
import {ArrowLeft, MapPin, Home, Users, FileImage, Download, ChevronLeft, IndianRupee} from "lucide-react";
import { getPresignedUrlFromApplicationId } from "../../features/createApplication/service/uploadFileService";
import { encodeURIComponent } from "@lib/utils";
import { getResidenceVerification } from "../../v2/features/residence-verification/services/rv-service";

interface Address {
  type?: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  country: string;
  latitude: number;
  longitude: number;
  active: boolean;
  verified: boolean;
  postalCode: number;
  ownership: string;
  monthlyRent: number;
  yearsOfStay: number;
}

interface AdditionalResidentialDetails {
  spouseName: string;
  spouseAwareOfLoan: boolean;
  livingStandard: string;
  accessibility: string;
  electricityBillNo?: string;
  electricityBillAmount?: number;
  overdueElectricityBillAmount?: number;
  gatedHouse: boolean;
  kitchen: boolean;
  communityDominatedArea: boolean;
  earningMemberCount: number;
  occupantsCount: number;
  childrenStudyingCount: number;
  roomsCount: number;
  dependentMemberCount: number;
  monthlyResidentialExpenses?: number;
  appliedLoanAmount: number;
  applicantStayStatus: string;
  addressIsSameAsKYC: boolean;
}

interface VerificationData {
  id: string;
  resourceId: string;
  address: Address;
  additionalResidentialDetails: AdditionalResidentialDetails;
  stayStatus: string;
  thirdPartyReference: boolean;
  remarks: string;
  documents: string[];
  completedAt: string;
  completedBy: string;
  submitterRole: string;
  lastUpdatedAt: string;
}

export const ResidenceVerificationViewPage: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [verifications, setVerifications] = useState<VerificationData[]>([]);
  const [selectedVerification, setSelectedVerification] = useState<VerificationData | null>(null);
  const [verificationStatus, setVerificationStatus] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<any[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  useEffect(() => {
    const fetchVerifications = async () => {
      try {
        setLoading(true);
        if (!applicationId) {
          setVerifications([]);
          return;
        }

        const aggregateResponse = await getResidenceVerification(applicationId);
        if (!aggregateResponse?.aggregate || aggregateResponse.verificationStatus === "NOT_STARTED") {
          setVerifications([]);
          setVerificationStatus(aggregateResponse?.verificationStatus || "NOT_STARTED");
          return;
        }

        setVerificationStatus(aggregateResponse.verificationStatus);

        const aggregate = aggregateResponse.aggregate;
        const mappedVerification: VerificationData = {
          id: aggregate.completedAt || aggregate.lastUpdatedAt || new Date().toISOString(),
          resourceId: aggregate.applicationId,
          address: {
            type: aggregate.address?.type,
            line1: aggregate.address?.line1 || "",
            line2: aggregate.address?.line2 || "",
            city: aggregate.address?.city || "",
            state: aggregate.address?.state || "",
            country: aggregate.address?.country || "",
            latitude: aggregate.address?.latitude || 0,
            longitude: aggregate.address?.longitude || 0,
            active: aggregate.address?.active ?? true,
            verified: aggregate.address?.verified ?? aggregateResponse.verificationStatus === "COMPLETED",
            postalCode: Number(aggregate.address?.postalCode || 0),
            ownership: aggregate.address?.ownership || "",
            monthlyRent: Number(aggregate.address?.monthlyRent || 0),
            yearsOfStay: Number(aggregate.address?.yearsOfStay || 0),
          },
          additionalResidentialDetails: {
            spouseName: aggregate.additionalResidentialDetails?.spouseName || "",
            spouseAwareOfLoan: aggregate.additionalResidentialDetails?.spouseAwareOfLoan ?? false,
            livingStandard: aggregate.additionalResidentialDetails?.livingStandard || "",
            accessibility: aggregate.additionalResidentialDetails?.accessibility || "",
            electricityBillNo: aggregate.additionalResidentialDetails?.electricityBillNo || "",
            electricityBillAmount: Number(
              aggregate.additionalResidentialDetails?.electricityBillAmount || 0
            ),
            overdueElectricityBillAmount: Number(
              aggregate.additionalResidentialDetails?.overdueElectricityBillAmount || 0
            ),
            gatedHouse: aggregate.additionalResidentialDetails?.gatedHouse ?? false,
            kitchen: aggregate.additionalResidentialDetails?.kitchen ?? false,
            communityDominatedArea:
              aggregate.additionalResidentialDetails?.communityDominatedArea ?? false,
            earningMemberCount: Number(
              aggregate.additionalResidentialDetails?.earningMemberCount || 0
            ),
            occupantsCount: Number(aggregate.additionalResidentialDetails?.occupantsCount || 0),
            childrenStudyingCount: Number(
              aggregate.additionalResidentialDetails?.childrenStudyingCount || 0
            ),
            roomsCount: Number(aggregate.additionalResidentialDetails?.roomsCount || 0),
            dependentMemberCount: Number(aggregate.additionalResidentialDetails?.dependentMemberCount || 0),
            monthlyResidentialExpenses: Number(aggregate.additionalResidentialDetails?.monthlyResidentialExpenses || 0),
            appliedLoanAmount: Number(aggregate.additionalResidentialDetails?.appliedLoanAmount || 0),
            applicantStayStatus: aggregate.additionalResidentialDetails?.applicantStayStatus || "",
            addressIsSameAsKYC: aggregate.additionalResidentialDetails?.addressIsSameAsKYC ?? false,
          },
          stayStatus: aggregate.stayStatus || "",
          thirdPartyReference: aggregate.thirdPartyReference ?? false,
          remarks: aggregate.remarks || "",
          documents: aggregate.documents || [],
          completedAt: aggregate.completedAt || "",
          completedBy: aggregate.completedBy || "",
          submitterRole: aggregate.submitterRole || "",
          lastUpdatedAt: aggregate.lastUpdatedAt || "",
        };

        setVerifications([mappedVerification]);
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to fetch verification data",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    void fetchVerifications();
  }, [applicationId, toast]);

  const handleVerificationSelect = async (verification: VerificationData) => {
    setSelectedVerification(verification);
    if (verification.documents && verification.documents.length > 0) {
      await fetchDocuments();
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoadingDocuments(true);
      const response = await getPresignedUrlFromApplicationId(applicationId!);
      const residenceVerificationDocs = (response.documents["RESIDENCE_VERIFICATION_FORM"] || [])
      .filter((doc: any) => 
        (doc.objectKey.toLowerCase().includes('.jpg') ||
         doc.objectKey.toLowerCase().includes('.jpeg') ||
         doc.objectKey.toLowerCase().includes('.png') ||
         doc.objectKey.toLowerCase().includes('.gif'))
        ).map((doc: any) => {
        // Extract file name from objectKey
        const fileName = doc.objectKey.split('/').pop();  // This gets the last part of the path
        // Add the fileName as a new property
        return {
            ...doc,
            fileName: fileName
        };
      });
      setDocuments(residenceVerificationDocs);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch documents",
        variant: "destructive",
      });
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleDownloadDocument = (presignedUrl: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = presignedUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBackToList = () => {
    setSelectedVerification(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (selectedVerification) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={handleBackToList}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to List
          </Button>
          <h1 className="text-2xl font-bold">Verification Details</h1>
          <Badge variant={verificationStatus === "COMPLETED" ? "default" : "secondary"}>
            {verificationStatus === "COMPLETED" ? "Completed" : verificationStatus === "IN_PROGRESS" ? "In Progress" : verificationStatus}
          </Badge>
        </div>

        <div className="grid gap-6">
          {/* Address Information */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="h-5 w-5" />
              <h3 className="text-lg font-semibold">Address Information</h3>
              {selectedVerification.address.type && (
                <Badge variant="secondary">{selectedVerification.address.type}</Badge>
              )}
              <Badge variant={selectedVerification.additionalResidentialDetails.addressIsSameAsKYC ? "default" : "secondary"}>
                {selectedVerification.additionalResidentialDetails.addressIsSameAsKYC ? "Same as KYC" : "Different from KYC"}
              </Badge>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Address</p>
                <p className="font-medium">
                  {selectedVerification.address.line1}, {selectedVerification.address.line2}
                </p>
                <p>{selectedVerification.address.city}, {selectedVerification.address.state}</p>
                <p>{selectedVerification.address.country} - {selectedVerification.address.postalCode}</p>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Ownership:</span>
                  <Badge variant={selectedVerification.address.ownership === "OWNED" ? "default" : "secondary"}>
                    {selectedVerification.address.ownership}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Monthly Rent:</span>
                  <span>₹{selectedVerification.address.monthlyRent.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Years of Stay:</span>
                  <span>{selectedVerification.address.yearsOfStay} years</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Verified:</span>
                  <Badge variant={selectedVerification.address.verified ? "default" : "destructive"}>
                    {selectedVerification.address.verified ? "Yes" : "No"}
                  </Badge>
                </div>
              </div>
            </div>
          </Card>

          {/* Household Information */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-5 w-5" />
              <h3 className="text-lg font-semibold">Household Information</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{selectedVerification.additionalResidentialDetails.occupantsCount}</p>
                <p className="text-sm text-gray-600">Total Occupants</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{selectedVerification.additionalResidentialDetails.earningMemberCount}</p>
                <p className="text-sm text-gray-600">Earning Members</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-orange-600">{selectedVerification.additionalResidentialDetails.childrenStudyingCount}</p>
                <p className="text-sm text-gray-600">Children Studying</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-purple-600">{selectedVerification.additionalResidentialDetails.dependentMemberCount}</p>
                <p className="text-sm text-gray-600">Dependent Members</p>
              </div>
            </div>
            {selectedVerification.additionalResidentialDetails.applicantStayStatus && (
              <div className="mt-4 flex justify-between">
                <span className="text-sm text-gray-600">Applicant Stay Status:</span>
                <Badge variant="outline">{selectedVerification.additionalResidentialDetails.applicantStayStatus}</Badge>
              </div>
            )}
          </Card>

          {/* Property Details */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Home className="h-5 w-5" />
              <h3 className="text-lg font-semibold">Property Details</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Rooms:</span>
                  <span>{selectedVerification.additionalResidentialDetails.roomsCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Kitchen:</span>
                  <Badge variant={selectedVerification.additionalResidentialDetails.kitchen ? "default" : "secondary"}>
                    {selectedVerification.additionalResidentialDetails.kitchen ? "Yes" : "No"}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Gated House:</span>
                  <Badge variant={selectedVerification.additionalResidentialDetails.gatedHouse ? "default" : "secondary"}>
                    {selectedVerification.additionalResidentialDetails.gatedHouse ? "Yes" : "No"}
                  </Badge>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Accessibility:</span>
                  <span>{selectedVerification.additionalResidentialDetails.accessibility}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Living Standard:</span>
                  <Badge variant="outline">{selectedVerification.additionalResidentialDetails.livingStandard}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Community Area:</span>
                  <Badge variant={selectedVerification.additionalResidentialDetails.communityDominatedArea ? "default" : "secondary"}>
                    {selectedVerification.additionalResidentialDetails.communityDominatedArea ? "Yes" : "No"}
                  </Badge>
                </div>
              </div>
            </div>
          </Card>

          {/* Financial Information */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <IndianRupee className="h-5 w-5" />
              <h3 className="text-lg font-semibold">Financial Information</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Electricity Bill No:</span>
                  <span>{selectedVerification.additionalResidentialDetails.electricityBillNo}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Bill Amount:</span>
                  <span>₹{selectedVerification.additionalResidentialDetails.electricityBillAmount?.toLocaleString()}</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Overdue Amount:</span>
                  <span className={(selectedVerification.additionalResidentialDetails.overdueElectricityBillAmount ?? 0) > 0 ? "text-red-600" : ""}>
                    ₹{selectedVerification.additionalResidentialDetails.overdueElectricityBillAmount?.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Monthly Residential Expenses:</span>
                  <span>₹{selectedVerification.additionalResidentialDetails.monthlyResidentialExpenses?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Applied Loan Amount:</span>
                  <span>₹{selectedVerification.additionalResidentialDetails.appliedLoanAmount?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Stay Status:</span>
                  <Badge variant="outline">{selectedVerification.stayStatus}</Badge>
                </div>
              </div>
            </div>
          </Card>

          {/* Additional Information */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Additional Information</h3>
            <div className="space-y-4">
              {selectedVerification.additionalResidentialDetails.spouseName && (
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-gray-600">Spouse Name: </span>
                    <span className="font-medium">{selectedVerification.additionalResidentialDetails.spouseName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Spouse Aware of Loan:</span>
                    <Badge variant={selectedVerification.additionalResidentialDetails.spouseAwareOfLoan ? "default" : "destructive"}>
                      {selectedVerification.additionalResidentialDetails.spouseAwareOfLoan ? "Yes" : "No"}
                    </Badge>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Third Party Reference:</span>
                <Badge variant={selectedVerification.thirdPartyReference ? "default" : "destructive"}>
                  {selectedVerification.thirdPartyReference ? "Positive" : "Negative"}
                </Badge>
              </div>
              {selectedVerification.remarks && (
                <div>
                  <p className="text-sm text-gray-600 mb-2">Remarks:</p>
                  <p className="text-sm bg-gray-50 p-3 rounded-lg">{selectedVerification.remarks}</p>
                </div>
              )}
              {(selectedVerification.completedAt || selectedVerification.lastUpdatedAt) && (
                <div className="border-t pt-4 mt-4 space-y-2">
                  {selectedVerification.completedBy && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Completed By:</span>
                      <span className="font-medium">{selectedVerification.completedBy}</span>
                    </div>
                  )}
                  {selectedVerification.submitterRole && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Submitter Role:</span>
                      <Badge variant="outline">{selectedVerification.submitterRole}</Badge>
                    </div>
                  )}
                  {selectedVerification.completedAt && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Completed At:</span>
                      <span>{new Date(selectedVerification.completedAt).toLocaleString()}</span>
                    </div>
                  )}
                  {selectedVerification.lastUpdatedAt && (
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Last Updated:</span>
                      <span>{new Date(selectedVerification.lastUpdatedAt).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>

          {/* Documents */}
          {selectedVerification.documents && selectedVerification.documents.length > 0 && (
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <FileImage className="h-5 w-5" />
                <h3 className="text-lg font-semibold">Documents</h3>
                <Badge variant="outline">{selectedVerification.documents.length} files</Badge>
              </div>
              {loadingDocuments ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {documents.map((doc, index) => (
                    <div key={index} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium truncate">{doc.fileName}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadDocument(doc.presignedUrl, doc.fileName)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                      <img
                        src={doc.presignedUrl}
                        alt={doc.fileName}
                        className="w-full h-32 object-cover rounded border"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/dashboard/application/details/${encodeURIComponent(applicationId!)}`)}
          className="flex items-center gap-2 mb-4"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="sm:inline">Application</span>
        </Button>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Residence Verifications</h1>
          <Badge variant={verificationStatus === "COMPLETED" ? "default" : "secondary"}>
            {verificationStatus === "COMPLETED" ? "Completed" : verificationStatus === "IN_PROGRESS" ? "In Progress" : "Not Started"}
          </Badge>
        </div>
      </div>

      {verifications.length === 0 ? (
        <Card className="p-8 text-center space-y-4">
          <Home className="h-12 w-12 text-gray-300 mx-auto" />
          <p className="text-gray-500">
            Residence verification has not been started for this application yet.
          </p>
          <Button
            variant="outline"
            onClick={() => navigate(`/dashboard/residence-verification/${applicationId}`)}
          >
            Start Residence Verification
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4">
          {verifications.map((verification) => (
            <Card key={verification.id} className="p-6 hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleVerificationSelect(verification)}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="h-4 w-4 text-gray-500" />
                    <span className="font-medium">
                      {verification.address.line1}, {verification.address.city}
                    </span>
                    {verification.address.type && (
                      <Badge variant="secondary" className="text-xs">{verification.address.type}</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span>{verification.address.state}, {verification.address.country}</span>
                    <span>•</span>
                    <span>{verification.address.ownership}</span>
                    <span>•</span>
                    <span>{verification.additionalResidentialDetails.occupantsCount} occupants</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Badge variant={verification.address.verified ? "default" : "destructive"}>
                    {verification.address.verified ? "Verified" : "Unverified"}
                  </Badge>
                  <Button variant="outline" size="sm">
                    View Details
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};